import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from "@mui/material";
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getInstituteLevelNavigation,
  getConfigurationNavigation,
  getSecondaryNavigation,
} from "../components.helpers";
import { IAppNavigationMenuItem } from "../components.interfaces";
import { DrawerToRenderProps } from "./navigationDrawer.interfaces";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { pages } from "../../../utils/common";
import {
  useAuthUserActions,
  useAuthUserState,
} from "../../../context/authUser.context";
import { canAccessInstituteLevelPages } from "../../../utils/userTypes";

type pageStatusType = { [key: string]: boolean };

const DrawerToRender: FC<DrawerToRenderProps> = (
  props: DrawerToRenderProps,
) => {
  const { selectedPage, isMenuOpen } = props;
  const navigate = useNavigate();
  const { logout } = useAuthUserActions();
  const { userDetails } = useAuthUserState();
  const canAccessInstitutePages = canAccessInstituteLevelPages(userDetails);

  const instituteLevelNavigation = useMemo(
    () => getInstituteLevelNavigation(),
    [],
  );
  const configurationNavigation = useMemo(
    () => getConfigurationNavigation(),
    [],
  );
  const secondaryPrimarySection = useMemo(() => getSecondaryNavigation(), []);
  const [pagesWithSubmenu, setPagesWithSubmenu] = useState<pageStatusType>({});

  // Combine navigation sections for submenu tracking
  const allPrimaryNavigation = useMemo(() => {
    return [
      ...(canAccessInstitutePages ? instituteLevelNavigation : []),
      ...configurationNavigation,
    ];
  }, [
    canAccessInstitutePages,
    instituteLevelNavigation,
    configurationNavigation,
  ]);

  const handlePageNavigation = useCallback(
    (path: string) => {
      if (pages.logout.path === path || pages.logout.aliasPath === path) {
        logout();
        navigate(pages.auth.path);
      } else if (pages.auth.path === path) {
        sessionStorage.clear();
        navigate(path);
      } else {
        navigate(path);
      }
    },
    [navigate, logout],
  );

  const updateSubMenuStatus = (
    pageToNavigate: string,
    isAlreadyExpanded = false,
  ) => {
    setPagesWithSubmenu((prev) => {
      const updatedPagesWithSubmenu: pageStatusType = {};
      Object.keys(prev).forEach((key) => {
        updatedPagesWithSubmenu[key] =
          key === pageToNavigate ? (isAlreadyExpanded ? false : true) : false;
      });
      return updatedPagesWithSubmenu;
    });
  };

  useEffect(() => {
    onUpdatePagesWithSubmenu();
    // eslint-disable-next-line
  }, [allPrimaryNavigation, selectedPage]);

  const checkIfItemOrChildMatches = useCallback(
    (item: IAppNavigationMenuItem, parentRoute: string): boolean => {
      const fullPath = parentRoute + item.pageToNavigate;
      if (fullPath === selectedPage || item.pageToNavigate === selectedPage) {
        return true;
      }
      if (item.subPages) {
        return item.subPages.some((subItem) =>
          checkIfItemOrChildMatches(subItem, fullPath),
        );
      }
      return false;
    },
    [selectedPage],
  );

  const onUpdatePagesWithSubmenu = useCallback(() => {
    setPagesWithSubmenu(
      allPrimaryNavigation.reduce((acc: pageStatusType, item) => {
        if (item.subPages) {
          acc[item.pageToNavigate] = checkIfItemOrChildMatches(item, "");
        }
        return acc;
      }, {}),
    );
  }, [allPrimaryNavigation, selectedPage, checkIfItemOrChildMatches]);

  const isItemSelected = useCallback(
    (item: IAppNavigationMenuItem, parentRoute: string) => {
      const fullPath = parentRoute + item.pageToNavigate;
      if (fullPath === selectedPage || item.pageToNavigate === selectedPage) {
        return true;
      }
      // Nested routes (e.g. /academics/courses) must still highlight the section root (/academics)
      if (fullPath && fullPath !== "/") {
        return selectedPage.startsWith(`${fullPath}/`);
      }
      return false;
    },
    [selectedPage],
  );

  const getEachListItem = useCallback(
    (
      item: IAppNavigationMenuItem,
      indentLevel: number,
      parentRoute: string,
    ) => {
      const fullPath = parentRoute + item.pageToNavigate;
      const selected = isItemSelected(item, parentRoute);

      return (
        <ListItemButton
          key={item.pageToNavigate}
          data-pl={indentLevel}
          selected={selected}
          data-testid={`nav-item-${item.pageToNavigate.replace(/\//g, "-").replace(/^-/, "")}`}
          sx={{
            pl: isMenuOpen ? indentLevel * 2 : 3,
          }}
          onClick={() => {
            item?.subPages &&
              updateSubMenuStatus(
                item.pageToNavigate,
                pagesWithSubmenu[item.pageToNavigate],
              );
            fullPath !== selectedPage && handlePageNavigation(fullPath);
          }}
        >
          {isMenuOpen ? (
            <ListItemIcon sx={{ minWidth: 36 }}>
              {item.icon}
            </ListItemIcon>
          ) : (
            <Tooltip title={item.title}>
              <ListItemIcon
                sx={{
                  minWidth: "var(--common-title-dimension)",
                }}
              >
                {item.icon}
              </ListItemIcon>
            </Tooltip>
          )}
          {isMenuOpen ? (
            <>
              <ListItemText
                primary={item.title}
                sx={{ opacity: isMenuOpen ? 1 : 0 }}
              />
              {pagesWithSubmenu[item.pageToNavigate] ? (
                <ExpandLess />
              ) : item.subPages ? (
                <ExpandMore />
              ) : null}
            </>
          ) : null}
        </ListItemButton>
      );
    },
    [
      handlePageNavigation,
      isMenuOpen,
      pagesWithSubmenu,
      selectedPage,
      isItemSelected,
    ],
  );

  const getEachListItemWithSubMenu = useCallback(
    (
      item: IAppNavigationMenuItem,
      indentLevel: number,
      parentRoute: string,
    ) => (
      <Fragment>
        {getEachListItem(item, indentLevel, parentRoute)}
        {item?.subPages ? (
          <Collapse
            in={pagesWithSubmenu[item.pageToNavigate]}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {item?.subPages?.map((subPagesItem: IAppNavigationMenuItem) =>
                subPagesItem.subPages
                  ? getEachListItemWithSubMenu(
                      subPagesItem,
                      indentLevel + 1,
                      parentRoute + item.pageToNavigate,
                    )
                  : getEachListItem(
                      subPagesItem,
                      indentLevel + 1,
                      parentRoute + item.pageToNavigate,
                    ),
              )}
            </List>
          </Collapse>
        ) : null}
      </Fragment>
    ),
    [pagesWithSubmenu, getEachListItem],
  );

  const renderNavigationItems = (items: IAppNavigationMenuItem[]) => {
    return items.map((item: IAppNavigationMenuItem) =>
      !item?.subPages ? (
        <Fragment key={item.pageToNavigate}>
          {getEachListItem(item, 1, "")}
        </Fragment>
      ) : (
        <Fragment key={item.pageToNavigate}>
          {getEachListItemWithSubMenu(item, 1, "")}
        </Fragment>
      ),
    );
  };

  return (
    <Box data-testid="navigation-drawer">
      {/* Institute-level navigation group (only for standard users) */}
      {canAccessInstitutePages && instituteLevelNavigation.length > 0 && (
        <>
          <List
            data-testid="main-menu-institute-level-section"
            subheader={
              isMenuOpen ? (
                <ListSubheader component="div" disableSticky>
                  Institute
                </ListSubheader>
              ) : null
            }
          >
            {renderNavigationItems(instituteLevelNavigation)}
          </List>
          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />
        </>
      )}

      {/* Configuration navigation group */}
      <List
        data-testid="main-menu-configuration-section"
        subheader={
          isMenuOpen ? (
            <ListSubheader component="div" disableSticky>
              Configuration
            </ListSubheader>
          ) : null
        }
      >
        {renderNavigationItems(configurationNavigation)}
      </List>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />
      <List
        data-testid="main-menu-secondary-section"
        subheader={
          isMenuOpen ? (
            <ListSubheader component="div" disableSticky>
              General
            </ListSubheader>
          ) : null
        }
      >
        {secondaryPrimarySection.map((item: IAppNavigationMenuItem) => (
          <Fragment key={item.pageToNavigate}>
            {getEachListItem(item, 1, "")}
          </Fragment>
        ))}
      </List>
    </Box>
  );
};

export default DrawerToRender;
